import { Skeleton } from '@folio/components/ui';
import type { Field } from 'korint-sdk/react';
import { X } from 'lucide-react';
import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BlockingErrorBanner } from '../blocking-error-banner/BlockingErrorBanner';
import { RequiredAsterisk } from '../RequiredAsterisk/RequiredAsterisk';
import { WarningBanner } from '../warning-banner/WarningBanner';
import { evaluateCondition } from './conditionalVisibilityUtils';
import {
  FormControl,
  FormDescription,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
} from '../form';

interface FormBuilderFieldProps {
  field: Field;
  children: React.ReactNode;
  isLoading?: boolean;
}

export function FormBuilderField({ field, children, isLoading }: FormBuilderFieldProps) {
  const { t } = useTranslation();
  const form = useFormContext();

  const { error } = useFormField();

  const fieldValue = useWatch({
    name: field.name,
    disabled: !(field.canBeDeleted && field.isCustomField) && field.blockingValue === undefined,
  });

  const blockingConditionNames = useMemo(
    () => (field.blockingWhen ?? []).map(condition => condition.fieldName ?? field.name),
    [field.blockingWhen, field.name],
  );

  const blockingWatchedValues = useWatch({
    name: blockingConditionNames,
    disabled: blockingConditionNames.length === 0,
  });

  const showBlockingBanner =
    (field.blockingValue !== undefined && fieldValue === field.blockingValue) ||
    (!!field.blockingWhen?.length &&
      field.blockingWhen.every((condition, index) =>
        evaluateCondition(condition, blockingWatchedValues[index]),
      ));

  const warningConditionNames = useMemo(
    () => (field.warningWhen ?? []).map(condition => condition.fieldName ?? field.name),
    [field.warningWhen, field.name],
  );

  const warningWatchedValues = useWatch({
    name: warningConditionNames,
    disabled: warningConditionNames.length === 0,
  });

  const showWarningBanner =
    !!field.warningWhen?.length &&
    field.warningWhen.every((condition, index) =>
      evaluateCondition(condition, warningWatchedValues[index]),
    );

  // AssetFunnelModule and CustomerFunnelModule format field names like so: {arrayName}.${index}.${actualFieldName}
  const actualFieldName =
    field.name.startsWith('assets.') || field.name.startsWith('customers.')
      ? field.name.split('.').slice(2).join('.')
      : field.name;

  const label =
    field.label ?? t([`fields.${actualFieldName}.form.label`, `fields.${actualFieldName}.label`]);

  /**
   * @debt field description is deprecated and should be removed once everything wording config was moved to i18n
   */
  const description = field.description ?? t([`fields.${actualFieldName}.description`, 'empty']);

  const showDeleteButton =
    field.canBeDeleted && field.isCustomField && fieldValue != null && fieldValue !== '';

  const isInlineToggle =
    field.type === 'radio' && (field as { display?: string }).display === 'toggle';

  return (
    <FormItem>
      {/* vehicleInput is a composite that renders its own tabs and field labels — no outer label. */}
      {field.type !== 'checkbox' && field.type !== 'vehicleInput' && label && (
        <div
          className={
            isInlineToggle
              ? 'flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2'
              : 'flex items-center justify-between'
          }
        >
          <FormLabel fieldType={field.type}>
            <span className="flex">
              <span className="block leading-relaxed whitespace-pre-line -mb-[0.3125em]">
                {label}
              </span>
              {field.required && <RequiredAsterisk />}
            </span>
          </FormLabel>
          {showDeleteButton && (
            <button
              type="button"
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive transition-colors"
              onClick={() =>
                form.setValue(field.name, null, { shouldDirty: true, shouldValidate: true })
              }
            >
              <X className="h-3.5 w-3.5" />
              {t('common.clear')}
            </button>
          )}
          {isInlineToggle && (
            <FormControl>{isLoading ? <Skeleton className="h-10 w-32" /> : children}</FormControl>
          )}
        </div>
      )}
      {!isInlineToggle && (
        <FormControl>{isLoading ? <Skeleton className="h-10 w-full" /> : children}</FormControl>
      )}
      {showBlockingBanner ? (
        <BlockingErrorBanner message={field.blockingErrorMessage ?? ''} />
      ) : error ? (
        field.blockingError && error.type === 'custom' ? (
          <BlockingErrorBanner message={error.message ?? ''} />
        ) : (
          <FormMessage />
        )
      ) : description ? (
        <FormDescription>{description}</FormDescription>
      ) : null}
      {showWarningBanner && !showBlockingBanner && (
        <WarningBanner message={field.warningMessage ?? ''} />
      )}
    </FormItem>
  );
}
